<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import BaseIcon from "@/Components/BaseIcon.vue";
import BaseButton from "@/Components/BaseButton.vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import {
    mdiBookOpenVariant, mdiBook, mdiPlusCircle, mdiPencil, mdiTrashCan, mdiPaperclip, mdiCheckBold, mdiFileDocumentOutline, mdiFileUpload, mdiDownload, mdiLink
} from "@mdi/js";

const props = defineProps({
    idClassroom: Number,
    idCourse: Number,
    ciclos: Array,
    canWrite: Boolean,
    refCicloActual: String,
    studentId: Number, // For student view
    courseName: String,
    classroomName: String,
    classroomYear: String,
    semanaActual: Number
});

import * as XLSX from 'xlsx';

const selectedCiclo = ref('');
const topics = ref([]);
const loading = ref(false);
const fileInput = ref(null);

const isModalTopicActive = ref(false);
const quillKey = ref(0);
const formTopic = ref({
    id: null,
    id_classroom: props.idClassroom,
    id_course: props.idCourse,
    id_ciclo: '',
    week_number: 1,
    sort_order: 1,
    start_date: '',
    end_date: '',
    title: '',
    description: '',
    content_full: '',
    status: 0
});

const isModalTaskActive = ref(false);
const formTask = ref({
    id: null,
    id_topic: null,
    title: '',
    type: 'tarea',
    description: '',
    due_date: '',
    max_points: 20,
    file: null,
    status: 0
});

const isModalResourceActive = ref(false);
const formResource = ref({
    id: null,
    id_topic: null,
    type: 'file',
    title: '',
    description: '',
    url: '',
    file: null
});

const isModalResponseActive = ref(false);
const currentTaskToRespond = ref(null);
const formResponse = ref({
    id_task: null,
    id_student: props.studentId,
    file: null,
    submission_text: ''
});

const isModalGradeActive = ref(false);
const currentTaskToGrade = ref(null);
const taskResponses = ref([]);

// UI State
const expandedTopics = ref({});

const initCiclo = () => {
    if (props.ciclos && props.ciclos.length > 0) {
        const current = props.ciclos.find(c => c.ref === props.refCicloActual);
        if (current) {
            selectedCiclo.value = current.id;
        } else {
            selectedCiclo.value = props.ciclos[0].id;
        }
        fetchTopics();
    }
};

const fetchTopics = async () => {
    loading.value = true;
    try {
        const params = {
            id_classroom: props.idClassroom,
            id_course: props.idCourse,
            id_ciclo: selectedCiclo.value
        };
        if (props.studentId) {
            params.student_id = props.studentId;
        }
        const res = await axios.get('/classroom-topics', { params });
        topics.value = res.data;
        // Expand current week by default
        topics.value.forEach(t => {
            if (expandedTopics.value[t.id] === undefined) {
                if (props.semanaActual) {
                    expandedTopics.value[t.id] = (t.week_number == props.semanaActual);
                } else {
                    expandedTopics.value[t.id] = true;
                }
            }
        });
    } catch (e) {
        console.error(e);
    }
    loading.value = false;
};

watch(selectedCiclo, () => {
    fetchTopics();
});

onMounted(() => {
    initCiclo();
});

// Topic Management
const openTopicModal = (topic = null) => {
    quillKey.value++;
    if (topic) {
        formTopic.value = { ...topic, status: topic.status !== undefined ? parseInt(topic.status) : 0 };
    } else {
        formTopic.value = {
            id: null,
            id_classroom: props.idClassroom,
            id_course: props.idCourse,
            id_ciclo: selectedCiclo.value,
            week_number: topics.value.length + 1,
            sort_order: topics.value.length + 1,
            start_date: '',
            end_date: '',
            title: '',
            description: '',
            content_full: '',
            status: 0
        };
    }
    isModalTopicActive.value = true;
};

const saveTopic = async () => {
    try {
        await axios.post('/classroom-topics', formTopic.value);
        isModalTopicActive.value = false;
        fetchTopics();
    } catch (e) {
        alert("Error al guardar el tema");
    }
};

const deleteTopic = async (topic) => {
    const hasTasks = topic.tasks && topic.tasks.length > 0;
    const hasResources = topic.resources && topic.resources.length > 0;

    if (hasTasks || hasResources) {
        alert("No se puede eliminar este tema porque contiene actividades o recursos de estudio asociados. Primero elimine estos elementos hijos por seguridad.");
        return;
    }

    if (confirm("¿Estás seguro de eliminar este tema?")) {
        await axios.delete(`/classroom-topics/${topic.id}`);
        fetchTopics();
    }
};

// Resource Management
const openResourceModal = (topicId) => {
    formResource.value = {
        id: null,
        id_topic: topicId,
        type: 'file',
        title: '',
        description: '',
        url: '',
        file: null
    };
    isModalResourceActive.value = true;
};

const handleResourceFileUpload = (e) => {
    formResource.value.file = e.target.files[0];
};

const saveResource = async () => {
    const formData = new FormData();
    for (let key in formResource.value) {
        if (formResource.value[key] !== null && formResource.value[key] !== '') {
            formData.append(key, formResource.value[key]);
        }
    }
    try {
        await axios.post('/classroom-topics/resource', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        isModalResourceActive.value = false;
        fetchTopics();
    } catch (e) {
        alert("Error al guardar recurso");
    }
};

const deleteResource = async (id) => {
    if (confirm("¿Seguro de eliminar este recurso?")) {
        await axios.delete(`/classroom-topics/resource/${id}`);
        fetchTopics();
    }
};

// Task Management
const openTaskModal = (topicId, task = null) => {
    if (task) {
        formTask.value = { ...task, file: null, status: task.status !== undefined ? parseInt(task.status) : 0 };
    } else {
        formTask.value = {
            id: null,
            id_topic: topicId,
            title: '',
            type: 'tarea',
            description: '',
            due_date: '',
            max_points: 20,
            file: null,
            status: 0
        };
    }
    isModalTaskActive.value = true;
};

const handleTaskFileUpload = (e) => {
    formTask.value.file = e.target.files[0];
};

const saveTask = async () => {
    const formData = new FormData();
    for (let key in formTask.value) {
        if (formTask.value[key] !== null && formTask.value[key] !== '') {
            formData.append(key, formTask.value[key]);
        }
    }
    try {
        await axios.post('/classroom-topics/task', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        isModalTaskActive.value = false;
        fetchTopics();
    } catch (e) {
        alert("Error al guardar tarea");
    }
};

const deleteTask = async (id) => {
    if (confirm("¿Seguro de eliminar esta actividad?")) {
        await axios.delete(`/classroom-topics/task/${id}`);
        fetchTopics();
    }
};

// Response (Student side)
const openResponseModal = (task) => {
    currentTaskToRespond.value = task;
    formResponse.value = {
        id_task: task.id,
        id_student: props.studentId,
        file: null,
        submission_text: task.responses && task.responses[0] ? (task.responses[0].submission_text || '') : ''
    };
    isModalResponseActive.value = true;
};

const handleResponseFileUpload = (e) => {
    formResponse.value.file = e.target.files[0];
};

const submitResponse = async () => {
    if (!formResponse.value.file && !formResponse.value.submission_text) {
        alert("Debes adjuntar un archivo o ingresar una respuesta escrita");
        return;
    }
    const formData = new FormData();
    formData.append('id_task', formResponse.value.id_task);
    formData.append('id_student', formResponse.value.id_student);
    if (formResponse.value.file) {
        formData.append('file', formResponse.value.file);
    }
    formData.append('submission_text', formResponse.value.submission_text || '');
    
    try {
        await axios.post('/classroom-topics/response', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        isModalResponseActive.value = false;
        fetchTopics();
        alert("Enviado correctamente");
    } catch (e) {
        alert("Error al enviar la respuesta");
    }
};

// Grading (Teacher side)
const openGradeModal = (task) => {
    currentTaskToGrade.value = task;
    taskResponses.value = task.responses || [];
    isModalGradeActive.value = true;
};

const saveGrade = async (responseObj) => {
    try {
        await axios.post('/classroom-topics/response', {
            id_task: responseObj.id_task,
            id_student: responseObj.id_student,
            grade: responseObj.grade,
            feedback: responseObj.feedback,
            status: 'graded'
        });
        alert("Nota guardada");
    } catch (e) {
        alert("Error al calificar");
    }
};

const reopenResponse = async (responseObj) => {
    if (confirm("¿Estás seguro de reabrir esta entrega para que el estudiante la mejore? Se borrará la nota actual.")) {
        try {
            await axios.post('/classroom-topics/response', {
                id_task: responseObj.id_task,
                id_student: responseObj.id_student,
                grade: null,
                feedback: responseObj.feedback,
                status: 'reopened'
            });
            responseObj.grade = null;
            responseObj.status = 'reopened';
            alert("Entrega reabierta con éxito");
            fetchTopics();
        } catch (e) {
            alert("Error al reabrir la entrega");
        }
    }
};

// EXCEL EXPORT
const exportToExcel = async () => {
    loading.value = true;
    try {
        const params = {
            id_classroom: props.idClassroom,
            id_course: props.idCourse
        };
        if (props.studentId) {
            params.student_id = props.studentId;
        }
        const res = await axios.get('/classroom-topics', { params });
        const allTopics = res.data;

        if (!allTopics.length) {
            alert("No hay temas registrados en ningún bimestre para exportar.");
            loading.value = false;
            return;
        }

        // 1. Create standard array of arrays
        let titleRow = [`${props.courseName || ''} - ${props.classroomName || ''} - ${props.classroomYear || ''}`];
        let emptyRow = [];
        let headersRow = ["BIMESTRE", "SEM.", "FECHA", "Nº TEMA", "TEMA", "CONTENIDOS BÁSICOS", "ESTADO TEMA", "ACTIVIDADES"];
        
        let wsData = [titleRow, emptyRow, headersRow];

        const stripHtml = (html) => {
            if (!html) return '';
            let tmp = document.createElement('div');
            tmp.innerHTML = html.replace(/<br\s*[\/]?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<p>/gi, '');
            return (tmp.textContent || tmp.innerText || '').trim();
        };

        // 2. Map topics
        allTopics.forEach(topic => {
            let fecha = (topic.start_date && topic.end_date) ? `${topic.start_date} a ${topic.end_date}` : '';
            const filteredTasks = (topic.tasks || []).map(task => ({
                title: task.title,
                type: task.type,
                description: task.description || '',
                due_date: task.due_date || '',
                max_points: task.max_points,
                status: task.status
            }));

            // Find the label matching the id_ciclo of the topic
            let cicloObj = props.ciclos.find(c => String(c.id) === String(topic.id_ciclo));
            let bimestreLabel = cicloObj ? cicloObj.label : 'I';

            let row = [
                bimestreLabel,
                `${topic.week_number}ª`,
                fecha,
                topic.sort_order,
                topic.title,
                stripHtml(topic.content_full),
                topic.status !== undefined ? topic.status : 0,
                JSON.stringify(filteredTasks)
            ];
            wsData.push(row);
        });

        // 3. Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Some column width adjustments
        ws['!cols'] = [
            { wch: 15 }, // BIMESTRE
            { wch: 8 },  // SEM.
            { wch: 15 }, // FECHA
            { wch: 8 },  // Nº TEMA
            { wch: 35 }, // TEMA
            { wch: 50 }, // CONTENIDOS BÁSICOS
            { wch: 12 }, // ESTADO TEMA
            { wch: 60 }, // ACTIVIDADES
        ];

        XLSX.utils.book_append_sheet(wb, ws, "Temario");

        // 4. Download
        let filename = `Temario_${props.courseName}_${props.classroomName}_Todos_Bimestres`.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.xlsx';
        XLSX.writeFile(wb, filename);
    } catch (e) {
        console.error(e);
        alert("Error al exportar a Excel");
    } finally {
        loading.value = false;
    }
};

// Helper to find the matched ciclo ID from text input
const findCicloId = (bimestreStr) => {
    if (!bimestreStr) return selectedCiclo.value;

    const normalized = String(bimestreStr).toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove accents
        .trim();

    // 1. Direct match on label or ref
    let match = props.ciclos.find(c => {
        const cLabel = String(c.label).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        const cRef = String(c.ref).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        return cLabel === normalized || cRef === normalized || cLabel.includes(normalized) || normalized.includes(cLabel);
    });

    if (match) return match.id;

    // 2. Match by numeric / word patterns
    if (normalized.includes("primer") || normalized.includes("b1") || /\b1(er|o|a)?\b/.test(normalized) || /\bi\b/.test(normalized)) {
        const found = props.ciclos.find(c => c.ref.endsWith('B1'));
        if (found) return found.id;
    }
    if (normalized.includes("segundo") || normalized.includes("b2") || /\b2(do|o|a)?\b/.test(normalized) || /\bii\b/.test(normalized)) {
        const found = props.ciclos.find(c => c.ref.endsWith('B2'));
        if (found) return found.id;
    }
    if (normalized.includes("tercer") || normalized.includes("b3") || /\b3(er|o|a)?\b/.test(normalized) || /\biii\b/.test(normalized)) {
        const found = props.ciclos.find(c => c.ref.endsWith('B3'));
        if (found) return found.id;
    }
    if (normalized.includes("cuarto") || normalized.includes("b4") || /\b4(to|o|a)?\b/.test(normalized) || /\biv\b/.test(normalized)) {
        const found = props.ciclos.find(c => c.ref.endsWith('B4'));
        if (found) return found.id;
    }

    // Default fallback to currently selected bimester
    return selectedCiclo.value;
};

// EXCEL IMPORT
const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    loading.value = true;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
        try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Get first worksheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to json, skip first 2 rows (header is on 3rd row, so range: 2 skips rows 0 and 1)
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: 2, defval: '' });
            
            let successCount = 0;
            for (let i = 0; i < jsonData.length; i++) {
                let row = jsonData[i];
                if (!row["TEMA"]) continue; // skip empty
                
                // Parse Sem. (e.g. "1ª" -> 1)
                let weekNumStr = row["SEM."] ? String(row["SEM."]).replace(/\D/g, '') : '';
                let weekNum = parseInt(weekNumStr) || 1;
                let sortOrder = parseInt(row["Nº TEMA"]) || (i + 1);
                
                let contentRaw = row["CONTENIDOS BÁSICOS"] || '';
                let contentHtml = contentRaw ? contentRaw.split('\n').map(line => `<p>${line}</p>`).join('') : '';

                let startDate = '';
                let endDate = '';
                if (row["FECHA"]) {
                    let dateParts = String(row["FECHA"]).split(' a ');
                    if (dateParts.length === 2) {
                        startDate = dateParts[0].trim();
                        endDate = dateParts[1].trim();
                    } else {
                        dateParts = String(row["FECHA"]).split('-');
                        if (dateParts.length === 2) {
                            startDate = dateParts[0].trim();
                            endDate = dateParts[1].trim();
                        }
                    }
                }

                let statusVal = 0;
                if (row["ESTADO TEMA"] !== undefined && row["ESTADO TEMA"] !== '') {
                    statusVal = parseInt(row["ESTADO TEMA"]);
                }

                let targetCicloId = findCicloId(row["BIMESTRE"]);

                let payload = {
                    id_classroom: props.idClassroom,
                    id_course: props.idCourse,
                    id_ciclo: targetCicloId,
                    week_number: weekNum,
                    sort_order: sortOrder,
                    start_date: startDate,
                    end_date: endDate,
                    title: row["TEMA"],
                    description: '',
                    content_full: contentHtml,
                    status: statusVal
                };
                
                try {
                    const response = await axios.post('/classroom-topics', payload);
                    successCount++;

                    const createdTopic = response.data.topic;

                    // Import activities / tasks if present
                    if (createdTopic && row["ACTIVIDADES"]) {
                        let activities = [];
                        try {
                            activities = JSON.parse(row["ACTIVIDADES"]);
                        } catch (err) {
                            console.error("Error parsing activities JSON:", err);
                        }

                        if (Array.isArray(activities)) {
                            for (let task of activities) {
                                let taskPayload = {
                                    id_topic: createdTopic.id,
                                    title: task.title,
                                    type: task.type || 'tarea',
                                    description: task.description || '',
                                    due_date: task.due_date || '',
                                    max_points: task.max_points || 20,
                                    status: task.status !== undefined ? parseInt(task.status) : 0
                                };
                                try {
                                    await axios.post('/classroom-topics/task', taskPayload);
                                } catch (taskErr) {
                                    console.error("Error creating task during import:", taskErr);
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.error("Error importing topic", row, e);
                }
            }
            
            alert(`Se importaron ${successCount} temas y sus actividades correctamente.`);
            fetchTopics();
        } catch (error) {
            console.error("Error parsing Excel:", error);
            alert("Hubo un error al leer el archivo Excel.");
        } finally {
            loading.value = false;
            if (fileInput.value) fileInput.value.value = null; // reset input
        }
    };
    reader.readAsArrayBuffer(file);
};

// Helper: formatDate
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const parts = dateStr.split('-');
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        const localDateStr = dateStr.includes('T') ? dateStr : dateStr.replace(/-/g, '/');
        const date = new Date(localDateStr);
        return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const localDateStr = dateStr.includes('T') ? dateStr : dateStr.replace(/-/g, '/');
        const date = new Date(localDateStr);
        return date.toLocaleDateString('es-PE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return dateStr;
    }
};

</script>

<template>
    <div>
        <div class="flex justify-between items-center mb-6">
            <div class="flex items-center gap-4">
                <BreezeLabel value="Bimestre / Ciclo" class="text-gray-700 font-bold" />
                <select v-model="selectedCiclo" class="border-gray-300 rounded-md shadow-sm">
                    <option v-for="c in ciclos" :key="c.id" :value="c.id">{{ c.label }}</option>
                </select>
            </div>
            <div class="flex gap-2">
                <BaseButton v-if="canWrite" :icon="mdiFileUpload" color="success" label="Importar de Excel" @click="$refs.fileInput.click()" />
                <BaseButton v-if="canWrite" :icon="mdiDownload" color="success" label="Exportar a Excel" @click="exportToExcel()" />
                <BaseButton v-if="canWrite" :icon="mdiPlusCircle" color="info" label="Nuevo Tema" @click="openTopicModal()" />
                <input type="file" ref="fileInput" class="hidden" accept=".xlsx, .xls" @change="handleImportExcel">
            </div>
        </div>

        <div v-if="loading" class="text-center py-6 text-gray-500">Cargando temas...</div>
        <div v-else-if="!topics.length" class="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
            No hay temas registrados para este bimestre.
        </div>

        <div v-else class="space-y-4">
            <div v-for="(topic, index) in topics" :key="topic.id" class="border rounded-lg bg-white shadow-sm overflow-hidden">
                <div class="flex justify-between items-center p-4 bg-gray-100 cursor-pointer" @click="expandedTopics[topic.id] = !expandedTopics[topic.id]">
                    <div class="flex items-center gap-2 font-bold text-lg text-gray-800">
                        <BaseIcon :path="expandedTopics[topic.id] ? mdiBookOpenVariant : mdiBook" :size="24" class="text-blue-600" />
                        Tema {{ topic.sort_order }} (Semana {{ topic.week_number }}): {{ topic.title }}
                        <span v-if="canWrite" :class="topic.status == 1 ? 'ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200' : 'ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200'">
                            {{ topic.status == 1 ? 'Validado' : 'Borrador' }}
                        </span>
                    </div>
                    <div class="flex items-center gap-2" @click.stop>
                        <BaseButton v-if="canWrite" :icon="mdiPencil" color="warning" small @click="openTopicModal(topic)" />
                        <BaseButton v-if="canWrite" :icon="mdiTrashCan" color="danger" small @click="deleteTopic(topic)" />
                    </div>
                </div>

                <div v-if="expandedTopics[topic.id]" class="p-4">
                    <div class="mb-4 flex gap-4 text-sm text-gray-600">
                        <span v-if="topic.start_date"><strong>Inicio:</strong> {{ formatDate(topic.start_date) }}</span>
                        <span v-if="topic.end_date"><strong>Fin:</strong> {{ formatDate(topic.end_date) }}</span>
                    </div>
                    <div class="prose max-w-none text-gray-700 text-sm mb-6" v-html="topic.content_full"></div>
                    
                    <div class="mt-4 border-t pt-4">
                        <div class="flex justify-between items-center mb-3">
                            <h4 class="font-bold text-gray-700">Recursos de Estudio</h4>
                            <BaseButton v-if="canWrite" :icon="mdiPlusCircle" color="success" small label="Agregar Recurso" @click="openResourceModal(topic.id)" />
                        </div>

                        <div v-if="!topic.resources || topic.resources.length === 0" class="text-sm text-gray-500 italic">
                            No hay recursos de estudio asignados.
                        </div>
                        <ul v-else class="space-y-3 mb-6">
                            <li v-for="resource in topic.resources" :key="resource.id" class="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                                <div>
                                    <div class="font-bold text-green-700 flex items-center gap-2">
                                        <BaseIcon :path="resource.type === 'file' ? mdiFileDocumentOutline : mdiLink" :size="20"/>
                                        {{ resource.title }}
                                    </div>
                                    <div v-if="resource.description" class="text-xs text-gray-500 mt-1">
                                        {{ resource.description }}
                                    </div>
                                    <div class="text-xs mt-1">
                                        <a v-if="resource.type === 'link'" :href="resource.url" target="_blank" class="text-blue-500 hover:underline flex items-center gap-1">
                                            <BaseIcon :path="mdiLink" :size="16"/> {{ resource.url }}
                                        </a>
                                        <a v-if="resource.type === 'file' && resource.file_path" :href="'/filedw/topic_resources/' + resource.id + '/' + resource.file_name" class="text-blue-500 hover:underline flex items-center gap-1">
                                            <BaseIcon :path="mdiPaperclip" :size="16"/> {{ resource.file_name }}
                                        </a>
                                    </div>
                                </div>
                                <div class="flex items-center gap-2" v-if="canWrite">
                                    <BaseButton :icon="mdiTrashCan" color="danger" small @click="deleteResource(resource.id)" />
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div class="mt-4 border-t pt-4">
                        <div class="flex justify-between items-center mb-3">
                            <h4 class="font-bold text-gray-700">Actividades (Tareas, Exámenes, Prácticas)</h4>
                            <BaseButton v-if="canWrite" :icon="mdiPlusCircle" color="info" small label="Agregar Actividad" @click="openTaskModal(topic.id)" />
                        </div>

                        <div v-if="!topic.tasks || topic.tasks.length === 0" class="text-sm text-gray-500 italic">
                            No hay actividades asignadas.
                        </div>
                        <ul v-else class="space-y-3">
                            <li v-for="task in topic.tasks" :key="task.id" class="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                                <div>
                                    <div class="font-bold text-blue-700 flex items-center gap-2">
                                        <BaseIcon :path="mdiFileDocumentOutline" :size="20"/>
                                        [{{ task.type.toUpperCase() }}] {{ task.title }}
                                        <span v-if="canWrite" :class="task.status == 1 ? 'ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200' : 'ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200'">
                                            {{ task.status == 1 ? 'Validado' : 'Borrador' }}
                                        </span>
                                    </div>
                                    <div class="text-xs text-gray-500 mt-1">
                                        Puntos: {{ task.max_points }} | Fecha Limite: {{ task.due_date ? task.due_date : 'Sin fecha' }}
                                    </div>
                                    <div v-if="task.file_path" class="text-xs mt-1">
                                        <a :href="'/filedw/task/' + task.id + '/' + task.file_name" class="text-blue-500 hover:underline flex items-center gap-1">
                                            <BaseIcon :path="mdiPaperclip" :size="16"/> {{ task.file_name }}
                                        </a>
                                    </div>
                                </div>
                                <div class="flex items-center gap-2">
                                    <template v-if="canWrite">
                                        <BaseButton color="info" small label="Calificar Entregas" @click="openGradeModal(task)" />
                                        <BaseButton :icon="mdiPencil" color="warning" small @click="openTaskModal(topic.id, task)" />
                                        <BaseButton :icon="mdiTrashCan" color="danger" small @click="deleteTask(task.id)" />
                                    </template>
                                    <template v-else-if="studentId">
                                        <div v-if="task.responses && task.responses[0]" class="flex items-center gap-4">
                                            <div class="text-right">
                                                <span :class="task.responses[0].grade !== null ? 'bg-green-100 text-green-800 border-green-200' : (task.responses[0].status === 'reopened' ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-blue-100 text-blue-800 border border-blue-200')" class="px-2 py-0.5 rounded text-xs font-semibold">
                                                    {{ task.responses[0].grade !== null ? 'Calificado' : (task.responses[0].status === 'reopened' ? 'Reabierto (Por mejorar)' : 'Entregado (Pendiente)') }}
                                                </span>
                                                <div v-if="task.responses[0].grade !== null" class="text-xs font-bold text-gray-700 mt-1">
                                                    Nota: <span class="text-indigo-600 font-extrabold">{{ parseFloat(task.responses[0].grade) }}</span> / {{ task.max_points }}
                                                </div>
                                                <div v-if="task.responses[0].submission_text" class="text-[11px] text-gray-600 mt-1 italic max-w-[200px] truncate" :title="task.responses[0].submission_text">
                                                    Mi Respuesta: "{{ task.responses[0].submission_text }}"
                                                </div>
                                                <div v-if="task.responses[0].feedback" class="text-[11px] text-gray-500 italic mt-0.5 max-w-[200px] truncate" :title="task.responses[0].feedback">
                                                    Feedback: "{{ task.responses[0].feedback }}"
                                                </div>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <a v-if="task.responses[0].file_path" :href="'/filedw/taskresp/' + task.responses[0].id + '/' + task.responses[0].file_name" class="text-xs text-blue-600 hover:underline flex items-center gap-1 bg-blue-50 px-2 py-1 rounded border border-blue-100">
                                                    <BaseIcon :path="mdiPaperclip" :size="14"/> Ver Entrega
                                                </a>
                                                <BaseButton v-if="task.responses[0].grade === null" color="warning" small label="Reenviar" @click="openResponseModal(task)" />
                                            </div>
                                        </div>
                                        <BaseButton v-else color="success" small label="Enviar Tarea" @click="openResponseModal(task)" />
                                    </template>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modals -->
        <CardBoxModal v-model="isModalTopicActive" :title="formTopic.id ? 'Editar Tema' : 'Nuevo Tema'" button="info" buttonLabel="Guardar" has-cancel @confirm="saveTopic">
            <div class="space-y-4">
                <div class="flex gap-4">
                    <div class="w-1/2">
                        <BreezeLabel value="Número de Tema (Orden)" />
                        <BreezeInput type="number" v-model="formTopic.sort_order" class="w-full" required />
                    </div>
                    <div class="w-1/2">
                        <BreezeLabel value="Semana" />
                        <BreezeInput type="number" v-model="formTopic.week_number" class="w-full" required />
                    </div>
                </div>
                <div class="flex gap-4">
                    <div class="w-1/2">
                        <BreezeLabel value="Fecha de Inicio" />
                        <BreezeInput type="date" v-model="formTopic.start_date" class="w-full" />
                    </div>
                    <div class="w-1/2">
                        <BreezeLabel value="Fecha de Fin" />
                        <BreezeInput type="date" v-model="formTopic.end_date" class="w-full" />
                    </div>
                </div>
                <div>
                    <BreezeLabel value="Estado de Publicación" />
                    <select v-model="formTopic.status" class="w-full border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm">
                        <option :value="0">Borrador</option>
                        <option :value="1">Validado (Visible para alumnos)</option>
                    </select>
                </div>
                <div>
                    <BreezeLabel value="Título" />
                    <BreezeInput type="text" v-model="formTopic.title" class="w-full" required />
                </div>
                <div>
                    <BreezeLabel value="Contenido" />
                    <QuillEditor :key="quillKey" v-model:content="formTopic.content_full" contentType="html" theme="snow" class="bg-white min-h-[200px]" />
                </div>
            </div>
        </CardBoxModal>

        <CardBoxModal v-model="isModalTaskActive" :title="formTask.id ? 'Editar Actividad' : 'Nueva Actividad'" button="info" buttonLabel="Guardar" has-cancel @confirm="saveTask">
            <div class="space-y-4">
                <div>
                    <BreezeLabel value="Tipo de Actividad" />
                    <select v-model="formTask.type" class="w-full border-gray-300 rounded-md shadow-sm">
                        <option value="tarea">Tarea / Deber</option>
                        <option value="examen">Examen / Prueba</option>
                        <option value="practica">Práctica / Taller</option>
                    </select>
                </div>
                <div>
                    <BreezeLabel value="Título" />
                    <BreezeInput type="text" v-model="formTask.title" class="w-full" required />
                </div>
                <div>
                    <BreezeLabel value="Fecha Límite" />
                    <BreezeInput type="datetime-local" v-model="formTask.due_date" class="w-full" />
                </div>
                <div>
                    <BreezeLabel value="Puntaje Máximo" />
                    <BreezeInput type="number" v-model="formTask.max_points" class="w-full" />
                </div>
                <div>
                    <BreezeLabel value="Adjuntar Archivo" />
                    <input type="file" @change="handleTaskFileUpload" class="w-full border p-2 rounded-md" />
                </div>
                <div>
                    <BreezeLabel value="Estado de Publicación" />
                    <select v-model="formTask.status" class="w-full border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm">
                        <option :value="0">Borrador</option>
                        <option :value="1">Validado (Visible para alumnos)</option>
                    </select>
                </div>
            </div>
        </CardBoxModal>

        <CardBoxModal v-model="isModalResponseActive" title="Enviar Tarea" button="success" buttonLabel="Enviar" has-cancel @confirm="submitResponse">
            <div class="space-y-4">
                <p><strong>Tarea:</strong> {{ currentTaskToRespond?.title }}</p>
                <div>
                    <BreezeLabel value="Respuesta escrita o comentario (Opcional)" />
                    <textarea v-model="formResponse.submission_text" rows="3" class="w-full border border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm p-2 text-sm" placeholder="Escribe tu respuesta o comentario aquí..."></textarea>
                </div>
                <div>
                    <BreezeLabel value="Archivo de Resolución (Opcional)" />
                    <input type="file" @change="handleResponseFileUpload" class="w-full border p-2 rounded-md" />
                </div>
            </div>
        </CardBoxModal>

        <CardBoxModal v-model="isModalGradeActive" title="Calificar Entregas" button="info" buttonLabel="Cerrar" @confirm="isModalGradeActive = false">
            <div class="space-y-4 max-h-[60vh] overflow-y-auto">
                <div v-if="!taskResponses.length" class="text-gray-500 italic">No hay entregas registradas.</div>
                <div v-else v-for="resp in taskResponses" :key="resp.id" class="p-3 border rounded-md bg-gray-50">
                    <div class="flex justify-between items-center mb-2">
                        <span class="font-bold">
                            {{ resp.student?.person ? `${resp.student.person.apellidos}, ${resp.student.person.nombres}` : `Estudiante ID: ${resp.id_student}` }}
                        </span>
                        <a v-if="resp.file_path" :href="'/filedw/taskresp/' + resp.id + '/' + resp.file_name" class="text-blue-500 hover:underline flex items-center gap-1">
                            <BaseIcon :path="mdiDownload" :size="16"/> Descargar Entrega
                        </a>
                    </div>
                    <!-- Display student's written response / comment -->
                    <div v-if="resp.submission_text" class="mb-3 p-2.5 bg-white border border-gray-200 rounded-md text-sm text-gray-700">
                        <span class="block text-xs font-bold text-gray-500 mb-1">Respuesta Escrita:</span>
                        <p class="whitespace-pre-line">{{ resp.submission_text }}</p>
                    </div>
                    <div class="flex gap-2">
                        <div class="w-1/4">
                            <BreezeLabel value="Nota" />
                            <BreezeInput type="number" v-model="resp.grade" class="w-full" />
                        </div>
                        <div class="w-2/4">
                            <BreezeLabel value="Feedback" />
                            <BreezeInput type="text" v-model="resp.feedback" class="w-full" />
                        </div>
                        <div class="w-1/4 flex items-end gap-1">
                            <BaseButton color="success" label="Guardar" @click="saveGrade(resp)" :class="resp.grade !== null ? 'w-1/2' : 'w-full'" />
                            <BaseButton v-if="resp.grade !== null" color="warning" label="Reabrir" @click="reopenResponse(resp)" class="w-1/2" title="Reabrir entrega para mejora" />
                        </div>
                    </div>
                </div>
            </div>
        </CardBoxModal>
        <CardBoxModal v-model="isModalResourceActive" title="Nuevo Recurso de Estudio" button="info" buttonLabel="Guardar" has-cancel @confirm="saveResource">
            <div class="space-y-4">
                <div>
                    <BreezeLabel value="Tipo de Recurso" />
                    <select v-model="formResource.type" class="w-full border-gray-300 rounded-md shadow-sm">
                        <option value="file">Archivo Adjunto</option>
                        <option value="link">Enlace Web</option>
                    </select>
                </div>
                <div>
                    <BreezeLabel value="Título" />
                    <BreezeInput type="text" v-model="formResource.title" class="w-full" required />
                </div>
                <div>
                    <BreezeLabel value="Descripción (Opcional)" />
                    <BreezeInput type="text" v-model="formResource.description" class="w-full" />
                </div>
                <div v-if="formResource.type === 'link'">
                    <BreezeLabel value="URL del Enlace" />
                    <BreezeInput type="url" v-model="formResource.url" class="w-full" placeholder="https://" />
                </div>
                <div v-if="formResource.type === 'file'">
                    <BreezeLabel value="Adjuntar Archivo" />
                    <input type="file" @change="handleResourceFileUpload" class="w-full border p-2 rounded-md" />
                </div>
            </div>
        </CardBoxModal>
    </div>
</template>
